import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BlogCategory, BlogPost } from '../../types/Blog';
import { getBlogCategoriesFromServer, getBlogPostsFromServer } from '../../utils/BlogAPI';
import { saveBlogCategoriesToServer } from '../../utils/BlogCategoryAPI';
import './BlogManagement.css';
import './BlogCategoryManagement.css';

const RESERVED = new Set(['page', 'category', 'preview', 'feed']);
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const FALLBACK: BlogCategory[] = [
  {
    id: 'family',
    name: 'Family',
    slug: 'family',
    intro: 'Notes on photographing families in Cape Town — locations, light, and what to expect from a session.',
    seoTitle: 'Family Photography Blog | Cape Town Photographer',
    metaDescription: 'Stories and practical notes on family photography in Cape Town, from Carmen Lorraine.',
    h1: 'Family',
  },
  {
    id: 'business-headshots',
    name: 'Business & Headshots',
    slug: 'business-headshots',
    intro: 'Headshots and business portraits in Cape Town: how a session works and how the pictures get used.',
    seoTitle: 'Business & Headshot Photography Blog | Cape Town',
    metaDescription: 'Advice on business portraits and headshots in Cape Town, written by Carmen Lorraine.',
    h1: 'Business & Headshots',
  },
  {
    id: 'lifestyle',
    name: 'Lifestyle',
    slug: 'lifestyle',
    intro: 'Lifestyle sessions in Cape Town — brand stories, everyday portraits, and photographs with room to breathe.',
    seoTitle: 'Lifestyle Photography Blog | Cape Town Photographer',
    metaDescription: 'Lifestyle photography stories from Cape Town by Carmen Lorraine, with notes on sessions.',
    h1: 'Lifestyle',
  },
  {
    id: 'natural',
    name: 'Natural',
    slug: 'natural',
    intro: 'Photographing outdoors around Cape Town: farms, coast, and natural light.',
    seoTitle: 'Natural Light Photography Blog | Cape Town',
    metaDescription: 'Outdoor and natural-light photography notes from Cape Town by Carmen Lorraine.',
    h1: 'Natural',
  },
];

interface Draft {
  id: string;
  name: string;
  slug: string;
  h1: string;
  intro: string;
  seoTitle: string;
  metaDescription: string;
  slugTouched: boolean;
  headingTouched: boolean;
  isNew: boolean;
}

function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function storyCount(posts: BlogPost[], slug: string): number {
  return posts.filter((post) => post.category === slug || (post.categories || []).includes(slug)).length;
}

function emptyDraft(): Draft {
  return {
    id: '',
    name: '',
    slug: '',
    h1: '',
    intro: '',
    seoTitle: '',
    metaDescription: '',
    slugTouched: false,
    headingTouched: false,
    isNew: true,
  };
}

function draftFrom(category: BlogCategory): Draft {
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    h1: category.h1 || category.name,
    intro: category.intro || '',
    seoTitle: category.seoTitle || '',
    metaDescription: category.metaDescription || '',
    slugTouched: true,
    headingTouched: Boolean(category.h1 && category.h1 !== category.name),
    isNew: false,
  };
}

function Hint({ text }: { text: string }) {
  return <span className="blog-hint">{text}</span>;
}

const BlogCategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBlogCategoriesFromServer()
      .then((loaded) => setCategories(loaded.length ? loaded : FALLBACK))
      .catch(() => setCategories(FALLBACK));
    getBlogPostsFromServer().then(setPosts).catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    if (!draft) return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDraft(null);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus();
    };
  }, [draft]);

  const persist = async (next: BlogCategory[]) => {
    const ordered = next.map((category, index) => ({ ...category, order: index + 1 }));
    setSaving(true);
    setMessage('');
    const saved = await saveBlogCategoriesToServer(ordered);
    setSaving(false);
    if (!saved) {
      setMessage('Could not save categories.');
      return false;
    }
    setCategories(ordered);
    window.dispatchEvent(new Event('blog_categories_updated'));
    return true;
  };

  const saveDraft = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const name = draft.name.trim();
    const slug = slugify(draft.slug || name);
    if (!name) {
      setMessage('Add a title.');
      return;
    }
    if (!SLUG_PATTERN.test(slug) || RESERVED.has(slug)) {
      setMessage('Use a web address made of letters, numbers, and hyphens.');
      return;
    }
    const taken = categories.some((category) => category.slug === slug && category.id !== draft.id);
    if (taken) {
      setMessage('Another category already uses that address.');
      return;
    }
    const nextCategory: BlogCategory = {
      id: draft.isNew ? slug : draft.id,
      name,
      slug,
      h1: draft.h1.trim() || name,
      intro: draft.intro.trim(),
      seoTitle: draft.seoTitle.trim(),
      metaDescription: draft.metaDescription.trim(),
    };
    const next = draft.isNew
      ? [...categories, nextCategory]
      : categories.map((category) => (category.id === draft.id ? { ...category, ...nextCategory } : category));
    const saved = await persist(next);
    if (saved) setDraft(null);
  };

  const remove = async (category: BlogCategory) => {
    const count = storyCount(posts, category.slug);
    if (count > 0) return;
    if (!window.confirm(`Remove the category "${category.name}"?`)) return;
    await persist(categories.filter((item) => item.id !== category.id));
  };

  const title = draft?.seoTitle || '';
  const description = draft?.metaDescription || '';

  return (
    <section className="admin-section blog-category-editor">
      <div className="section-header">
        <h2>Blog categories</h2>
        <div className="section-controls">
          <button type="button" className="btn btn-primary" onClick={() => { setMessage(''); setDraft(emptyDraft()); }}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add category
          </button>
        </div>
      </div>
      <p className="blog-field-note">
        Each category is a section of the blog. Add one here, then choose it when you write a post.
      </p>
      {message && !draft ? <p className="blog-form-message">{message}</p> : null}
      <div className="blog-category-list">
        {categories.map((category) => {
          const count = storyCount(posts, category.slug);
          return (
            <article className="blog-category-row" key={category.id || category.slug}>
              <div>
                <h3>{category.name}</h3>
                <p>{(category.intro || '').trim() || 'No introduction yet'}</p>
                <p className="blog-category-meta">
                  <span>/blog/category/{category.slug}</span>
                  <span>{count === 1 ? '1 story' : `${count} stories`}</span>
                </p>
              </div>
              <div className="blog-category-actions">
                <button type="button" className="btn btn-primary btn-sm" aria-label={`Edit ${category.name}`} onClick={() => { setMessage(''); setDraft(draftFrom(category)); }}>
                  <i className="fas fa-edit" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  aria-label={`Remove ${category.name}`}
                  disabled={count > 0}
                  title={count > 0 ? 'Move the stories out of this category before removing it' : 'Remove category'}
                  onClick={() => remove(category)}
                >
                  <i className="fas fa-trash" aria-hidden="true"></i>
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {draft ? createPortal(
        <div
          className="blog-category-dialog-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setDraft(null);
          }}
        >
          <div
            className="blog-category-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="blog-category-dialog-title"
          >
            <div className="blog-category-dialog-header">
              <h3 id="blog-category-dialog-title">{draft.isNew ? 'Add category' : 'Edit category'}</h3>
              <button type="button" className="blog-category-dialog-close" aria-label="Close" onClick={() => setDraft(null)}>&times;</button>
            </div>
            <form className="blog-category-dialog-form" onSubmit={saveDraft}>
              <div className="blog-category-dialog-fields">
                <label className="blog-field">Title
                  <input
                    autoFocus
                    value={draft.name}
                    maxLength={80}
                    onChange={(event) => {
                      const name = event.target.value;
                      setDraft((current) => current && ({
                        ...current,
                        name,
                        slug: current.slugTouched ? current.slug : slugify(name),
                        h1: current.headingTouched ? current.h1 : name,
                      }));
                    }}
                  />
                  <Hint text="The name in the blog menu and on each story." />
                </label>
                <label className="blog-field">Web address
                  <input
                    value={draft.slug}
                    disabled={!draft.isNew}
                    onChange={(event) => setDraft((current) => current && ({
                      ...current,
                      slug: slugify(event.target.value),
                      slugTouched: true,
                    }))}
                  />
                  <Hint text={draft.isNew
                    ? 'Taken from the title. You can change it before the first save. It stays the same after that.'
                    : 'This address stays the same so existing links keep working.'}
                  />
                </label>
                <p className="blog-category-path">/blog/category/{draft.slug || '…'}</p>
                <label className="blog-field">Page heading
                  <input
                    value={draft.h1}
                    onChange={(event) => setDraft((current) => current && ({
                      ...current,
                      h1: event.target.value,
                      headingTouched: true,
                    }))}
                  />
                  <Hint text="The large heading on the category page. It follows the title until you write a different one." />
                </label>
                <label className="blog-field">Introduction
                  <textarea rows={3} value={draft.intro} onChange={(event) => setDraft((current) => current && ({ ...current, intro: event.target.value }))} />
                  <Hint text="The sentence under the heading. Say what these posts are about, and include Cape Town." />
                </label>
                <label className="blog-field">Google title
                  <input value={title} maxLength={70} onChange={(event) => setDraft((current) => current && ({ ...current, seoTitle: event.target.value }))} />
                  <Hint text="The blue headline in Google. Keep it under 60 characters." />
                </label>
                <p className={`blog-counter${title.length > 60 ? ' is-warn' : ''}`}>{title.length}{title.length > 60 ? ' · over 60' : ''}</p>
                <label className="blog-field">Meta description
                  <textarea rows={3} value={description} onChange={(event) => setDraft((current) => current && ({ ...current, metaDescription: event.target.value }))} />
                  <Hint text="The two lines under the headline in Google. 70 to 155 characters." />
                </label>
                <p className={`blog-counter${description.length > 0 && (description.length < 70 || description.length > 155) ? ' is-warn' : ''}`}>
                  {description.length}{description.length > 155 ? ' · over 155' : ''}{description.length > 0 && description.length < 70 ? ' · under 70' : ''}
                </p>
                <div className="blog-google-preview" aria-label="Google preview">
                  <p>{title || `${draft.name.trim() || 'Category'} | Cape Town Photographer`}</p>
                  <p>https://carmenlorraine.com/blog/category/{draft.slug || 'category'}</p>
                  <p>{description || 'The two lines under the headline in Google.'}</p>
                </div>
                {message ? <p className="blog-form-message">{message}</p> : null}
              </div>
                <div className="blog-category-dialog-actions">
                  <button type="button" className="is-secondary" onClick={() => setDraft(null)}>Cancel</button>
                  <button type="submit" className="is-primary" disabled={saving}>
                    {saving ? 'Saving…' : draft.isNew ? 'Save category' : 'Update category'}
                  </button>
                </div>
            </form>
          </div>
        </div>,
        document.body
      ) : null}
    </section>
  );
};

export default BlogCategoryManagement;
